/**
 * Module Installer Component
 *
 * UI for installing modules by entering a manifest hash or URL.
 */
import { useState } from 'react';
import type { ModuleLoadResult } from '@amorphous/core';
import './ModuleInstaller.css';

interface ModuleInstallerProps {
  /** Load a module from DAG */
  onLoadModule: (manifestHash: string) => Promise<ModuleLoadResult>;
  /** Close the installer */
  onClose: () => void;
}

export function ModuleInstaller({ onLoadModule, onClose }: ModuleInstallerProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = input.trim();
    if (!trimmed) {
      setError('Please enter a manifest hash');
      return;
    }

    // Extract hash from URL if needed
    let hash = trimmed;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      // Try to extract hash from URL
      const urlMatch = trimmed.match(/[#?]hash=([A-Za-z0-9]+)/);
      const pathMatch = trimmed.match(/\/module\/([A-Za-z0-9]+)/);
      if (urlMatch) {
        hash = urlMatch[1];
      } else if (pathMatch) {
        hash = pathMatch[1];
      } else {
        setError('Could not extract module hash from URL');
        return;
      }
    }

    // Validate hash format (base58)
    if (!/^[1-9A-HJ-NP-Za-km-z]+$/.test(hash)) {
      setError('Invalid manifest hash format');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await onLoadModule(hash);

      if (result.success) {
        setSuccess(true);
        setInput('');
        // Auto-close after short delay on success
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(result.errors.join('\n'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div className="module-installer-overlay" onClick={handleOverlayClick}>
      <div className="module-installer">
        <div className="module-installer-header">
          <h2>Install Module</h2>
          <button
            className="close-btn"
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="module-installer-body">
          <div className="input-group">
            <label htmlFor="manifest-input">Module Manifest Hash or URL</label>
            <input
              id="manifest-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Qm... or https://..."
              disabled={loading}
              autoFocus
            />
            <div className="input-hint">
              Enter a DAG hash or a URL containing the module manifest
            </div>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠</span>
              <span className="error-text">{error}</span>
            </div>
          )}

          {success && (
            <div className="success-message">
              <span className="success-icon">✓</span>
              <span className="success-text">Module installed successfully!</span>
            </div>
          )}

          <div className="module-installer-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-install"
              disabled={loading || !input.trim()}
            >
              {loading ? 'Loading...' : 'Install'}
            </button>
          </div>
        </form>

        <div className="module-installer-footer">
          <div className="security-note">
            <span className="security-icon">🔒</span>
            <span>
              Modules are verified against trusted signatures before installation.
              You'll be asked to approve any sensitive permissions.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
